import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea } from 'recharts';

// SVG donut arc path helper
function donutArc(cx, cy, outerR, innerR, startAngle, endAngle) {
  const gap = 0.02;
  const s = startAngle + gap;
  const e = endAngle - gap;
  if (e <= s) return '';
  const largeArc = (e - s) > Math.PI ? 1 : 0;
  return [
    `M ${cx + outerR * Math.cos(s)} ${cy + outerR * Math.sin(s)}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${cx + outerR * Math.cos(e)} ${cy + outerR * Math.sin(e)}`,
    `L ${cx + innerR * Math.cos(e)} ${cy + innerR * Math.sin(e)}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${cx + innerR * Math.cos(s)} ${cy + innerR * Math.sin(s)}`,
    'Z'
  ].join(' ');
}

const RED_SHADES = ['#991B1B', '#B91C1C', '#DC2626', '#EF4444', '#F87171', '#FCA5A5', '#FECACA'];

function Data() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const user = searchParams.get('user');

  const [repos, setRepos] = useState([]);
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Churn data
  const [fileChanges, setFileChanges] = useState({});
  const [weeklyData, setWeeklyData] = useState([]);
  const [fileWeeklyData, setFileWeeklyData] = useState({});
  const [churnN, setChurnN] = useState(21);
  const [churnLoading, setChurnLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0, phase: '' });

  const [showBudgetCalc, setShowBudgetCalc] = useState(false);
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [engineers, setEngineers] = useState([{ salary: 10000, period: 'mo' }]);
  const [tools, setTools] = useState([{ name: 'Cursor', cost: 20 }]);
  const [showFiles, setShowFiles] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);

  // File exclusion — lock files, generated files, configs, dotfiles
  const EXCLUDED_PATTERNS = [
    /package-lock\.json$/,
    /yarn\.lock$/,
    /pnpm-lock\.yaml$/,
    /composer\.lock$/,
    /Gemfile\.lock$/,
    /Cargo\.lock$/,
    /poetry\.lock$/,
    /\.min\.(js|css)$/,
    /\.map$/,
    /\.snap$/,
    /\.svg$/,
    /dist\//,
    /build\//,
    /\.next\//,
    /node_modules\//,
    /vendor\//,
    /\.generated\./,
    /generated\//,
    /__generated__\//,
    /\.g\.(ts|dart)$/,
    /\.pb\.(go|js|ts)$/,
  ];
  const isExcluded = (filename) => EXCLUDED_PATTERNS.some(p => p.test(filename));

  const TOOL_CATEGORIES = {
    'AI Coding': ['Cursor', 'Claude', 'GitHub Copilot', 'Copilot Workspace', 'Codex', 'Tabnine', 'Amazon Q', 'Windsurf', 'Sourcegraph Cody', 'Replit', 'CodeRabbit'],
    'Source Control': ['GitHub Enterprise', 'GitLab', 'Bitbucket'],
    'CI/CD & Hosting': ['CircleCI', 'Vercel', 'Railway', 'Netlify', 'AWS', 'GCP', 'Azure'],
    'Project Management': ['Jira', 'Linear', 'Asana', 'Shortcut', 'Monday'],
    'Docs & Comms': ['Notion', 'Confluence', 'Slack', 'Microsoft Teams'],
    'Monitoring': ['Datadog', 'Sentry', 'Grafana', 'New Relic', 'PagerDuty'],
    'Automation': ['n8n', 'Zapier', 'Retool'],
    'Design': ['Figma', 'Storybook'],
  };

  // Normalize engineer salaries to monthly
  const engMonthly = engineers.reduce((s, e) => s + ((e.salary || 0) / (e.period === 'yr' ? 12 : 1)), 0);
  const toolsMonthly = tools.reduce((s, t) => s + (t.cost || 0), 0);
  const engBudget = engMonthly + toolsMonthly;

  // Fetch user's repos on mount
  useEffect(() => {
    if (!token) {
      navigate('/connect');
      return;
    }

    const fetchRepos = async () => {
      try {
        const response = await fetch('https://api.github.com/user/repos?per_page=100&sort=updated', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/vnd.github.v3+json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch repos');
        }

        const data = await response.json();
        setRepos(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRepos();
  }, [token, navigate]);

  // Fetch file-level churn data when a repo is selected
  useEffect(() => {
    if (!selectedRepo || !token) return;

    const fetchChurnData = async () => {
      setChurnLoading(true);
      setFileChanges({});
      setWeeklyData([]);
      setFileWeeklyData({});
      setLoadingProgress({ current: 0, total: 0, phase: 'Fetching commits...' });

      const owner = selectedRepo.owner.login;
      const repo = selectedRepo.name;
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/vnd.github.v3+json',
      };

      try {
        // 1. Get commits from last 90 days
        const since = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
        let allCommits = [];
        let page = 1;

        while (true) {
          const res = await fetch(
            `https://api.github.com/repos/${owner}/${repo}/commits?since=${since}&per_page=100&page=${page}`,
            { headers }
          );
          if (!res.ok) break;
          const commits = await res.json();
          if (commits.length === 0) break;
          allCommits = [...allCommits, ...commits];
          if (commits.length < 100) break;
          page++;
        }

        setLoadingProgress({ current: 0, total: allCommits.length, phase: 'Analyzing commits...' });

        // 2. Fetch commit details in parallel batches
        const BATCH_SIZE = 10;
        const fileStats = {};
        const weeklyStats = {}; // { weekKey: { additions, deletions } }
        const fileWeekly = {}; // { filename: { weekKey: { additions, deletions } } }

        for (let i = 0; i < allCommits.length; i += BATCH_SIZE) {
          const batch = allCommits.slice(i, i + BATCH_SIZE);

          const details = await Promise.all(
            batch.map(async (commit) => {
              try {
                const res = await fetch(
                  `https://api.github.com/repos/${owner}/${repo}/commits/${commit.sha}`,
                  { headers }
                );
                if (res.ok) return res.json();
                return null;
              } catch {
                return null;
              }
            })
          );

          // Aggregate file changes and weekly data
          details.forEach((detail) => {
            if (!detail?.files) return;

            // Get week key from commit date
            const commitDate = new Date(detail.commit?.author?.date || detail.commit?.committer?.date);
            const weekStart = new Date(commitDate);
            weekStart.setDate(weekStart.getDate() - weekStart.getDay());
            const weekKey = weekStart.toISOString().split('T')[0];

            if (!weeklyStats[weekKey]) {
              weeklyStats[weekKey] = { week: weekKey, additions: 0, deletions: 0 };
            }

            detail.files.forEach((file) => {
              if (isExcluded(file.filename)) return;
              // File stats
              if (!fileStats[file.filename]) {
                fileStats[file.filename] = {
                  filename: file.filename,
                  additions: 0,
                  deletions: 0,
                  commits: 0,
                };
              }
              fileStats[file.filename].additions += file.additions || 0;
              fileStats[file.filename].deletions += file.deletions || 0;
              fileStats[file.filename].commits += 1;

              // Weekly stats
              weeklyStats[weekKey].additions += file.additions || 0;
              weeklyStats[weekKey].deletions += file.deletions || 0;

              // Per-file-per-week stats (for N-day survival calculation)
              if (!fileWeekly[file.filename]) fileWeekly[file.filename] = {};
              if (!fileWeekly[file.filename][weekKey]) fileWeekly[file.filename][weekKey] = { additions: 0, deletions: 0 };
              fileWeekly[file.filename][weekKey].additions += file.additions || 0;
              fileWeekly[file.filename][weekKey].deletions += file.deletions || 0;
            });
          });

          setLoadingProgress({ current: Math.min(i + BATCH_SIZE, allCommits.length), total: allCommits.length, phase: 'Analyzing commits...' });
        }

        // Convert weekly stats to sorted array (churn computed in useMemo)
        const weeks = Object.values(weeklyStats)
          .sort((a, b) => a.week.localeCompare(b.week));

        setFileChanges(fileStats);
        setWeeklyData(weeks);
        setFileWeeklyData(fileWeekly);
      } catch (err) {
        console.error('Error fetching churn data:', err);
      } finally {
        setChurnLoading(false);
      }
    };

    fetchChurnData();
  }, [selectedRepo, token]);

  // Compute N-day survival churn from cached per-file-per-week data
  // Per-file deletions capped at that file's additions — can only churn lines you wrote
  // Weeks whose N-day window hasn't closed yet are marked immature
  const weeklyChurnData = useMemo(() => {
    if (!weeklyData.length || !Object.keys(fileWeeklyData).length) return weeklyData;

    const allWeeks = weeklyData.map(w => w.week).sort();
    const now = Date.now();

    return weeklyData.map((weekEntry) => {
      const weekStart = new Date(weekEntry.week);
      const windowEnd = new Date(weekStart.getTime() + churnN * 24 * 60 * 60 * 1000);
      const mature = windowEnd.getTime() <= now;

      let totalAdditionsThisWeek = 0;
      let totalCappedDeletions = 0;

      for (const [, weekMap] of Object.entries(fileWeeklyData)) {
        const additionsThisWeek = weekMap[weekEntry.week]?.additions || 0;
        if (additionsThisWeek === 0) continue;

        totalAdditionsThisWeek += additionsThisWeek;

        let fileDeletions = 0;
        for (const otherWeek of allWeeks) {
          const otherDate = new Date(otherWeek);
          if (otherDate <= weekStart) continue;
          if (otherDate > windowEnd) break;
          fileDeletions += weekMap[otherWeek]?.deletions || 0;
        }
        // Cap: can't delete more new lines than were added
        totalCappedDeletions += Math.min(fileDeletions, additionsThisWeek);
      }

      const churn = totalAdditionsThisWeek > 0
        ? Math.round((totalCappedDeletions / totalAdditionsThisWeek) * 100)
        : 0;

      return { ...weekEntry, churn, mature, survivedAdditions: totalAdditionsThisWeek, churnedDeletions: totalCappedDeletions };
    });
  }, [weeklyData, fileWeeklyData, churnN]);

  // Aggregate churn from mature weeks only (window fully closed)
  const aggregateChurn = useMemo(() => {
    const matureWeeks = weeklyChurnData.filter(w => w.mature);
    const totalAdded = matureWeeks.reduce((s, w) => s + (w.survivedAdditions || 0), 0);
    const totalChurned = matureWeeks.reduce((s, w) => s + (w.churnedDeletions || 0), 0);
    const overallChurn = totalAdded > 0 ? Math.round((totalChurned / totalAdded) * 100) : 0;
    return { totalAdded, totalChurned, overallChurn, featurePct: 100 - overallChurn, matureWeekCount: matureWeeks.length };
  }, [weeklyChurnData]);

  // Per-file churn breakdown across mature weeks (for donut segments)
  const fileChurnBreakdown = useMemo(() => {
    if (!Object.keys(fileWeeklyData).length || !weeklyData.length) return [];

    const now = Date.now();
    const allWeeks = weeklyData.map(w => w.week).sort();
    const matureWeekKeys = weeklyData
      .filter(w => new Date(w.week).getTime() + churnN * 24 * 60 * 60 * 1000 <= now)
      .map(w => w.week);

    const fileStats = {};
    let totalChurned = 0;

    for (const [filename, weekMap] of Object.entries(fileWeeklyData)) {
      let fileChurned = 0;

      for (const weekKey of matureWeekKeys) {
        const additions = weekMap[weekKey]?.additions || 0;
        if (additions === 0) continue;

        const weekStart = new Date(weekKey);
        const windowEnd = new Date(weekStart.getTime() + churnN * 24 * 60 * 60 * 1000);

        let deletions = 0;
        for (const otherWeek of allWeeks) {
          const otherDate = new Date(otherWeek);
          if (otherDate <= weekStart) continue;
          if (otherDate > windowEnd) break;
          deletions += weekMap[otherWeek]?.deletions || 0;
        }
        fileChurned += Math.min(deletions, additions);
      }

      if (fileChurned > 0) {
        fileStats[filename] = fileChurned;
        totalChurned += fileChurned;
      }
    }

    return Object.entries(fileStats)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([filename, churned]) => ({
        filename: filename.split('/').pop(),
        fullPath: filename,
        churned,
        pct: totalChurned > 0 ? (churned / totalChurned) * 100 : 0,
      }));
  }, [fileWeeklyData, weeklyData, churnN]);

  // Per-file churn for the selected week (deletions capped at additions)
  const selectedWeekFiles = useMemo(() => {
    if (!selectedWeek || !Object.keys(fileWeeklyData).length || !weeklyData.length) return [];

    const allWeeks = weeklyData.map(w => w.week).sort();
    const weekStart = new Date(selectedWeek);
    const windowEnd = new Date(weekStart.getTime() + churnN * 24 * 60 * 60 * 1000);

    const files = [];
    for (const [filename, weekMap] of Object.entries(fileWeeklyData)) {
      const additions = weekMap[selectedWeek]?.additions || 0;
      if (additions === 0) continue;

      let rawDeletions = 0;
      for (const otherWeek of allWeeks) {
        const otherDate = new Date(otherWeek);
        if (otherDate <= weekStart) continue;
        if (otherDate > windowEnd) break;
        rawDeletions += weekMap[otherWeek]?.deletions || 0;
      }
      const cappedDeletions = Math.min(rawDeletions, additions);

      const churnRate = Math.round((cappedDeletions / additions) * 100);
      files.push({ filename, additions, deletions: cappedDeletions, churnRate });
    }

    return files.sort((a, b) => b.churnRate - a.churnRate);
  }, [selectedWeek, fileWeeklyData, weeklyData, churnN]);


  // Compute sorted file list, churn rates, and totals
  const { sortedFiles, totals, codebaseChurn } = useMemo(() => {
    const files = Object.values(fileChanges).map((file) => {
      // Churn = deletions / additions (what % of added code was later deleted/replaced)
      const churnRate = file.additions > 0
        ? Math.round((file.deletions / file.additions) * 100)
        : 0;
      return { ...file, churnRate };
    });

    const sorted = files.sort((a, b) => b.churnRate - a.churnRate);
    const totalAdditions = files.reduce((sum, f) => sum + f.additions, 0);
    const totalDeletions = files.reduce((sum, f) => sum + f.deletions, 0);
    const totalCommits = Object.keys(fileChanges).length > 0 ? loadingProgress.total : 0;

    // Codebase Churn = total deletions / total additions
    const codebaseChurnRate = totalAdditions > 0
      ? Math.round((totalDeletions / totalAdditions) * 1000) / 10
      : 0;

    return {
      sortedFiles: sorted,
      totals: { additions: totalAdditions, deletions: totalDeletions, commits: totalCommits, files: files.length },
      codebaseChurn: codebaseChurnRate,
    };
  }, [fileChanges, loadingProgress.total]);


  // Hybrid scale: linear 0–100, log above 100
  const toScale = (v) => v <= 100 ? v : 100 + Math.log10(v / 100) * 100;
  const fromScale = (v) => v <= 100 ? v : Math.round(100 * Math.pow(10, (v - 100) / 100));

  // Transform chart data for hybrid scale
  const chartData = useMemo(() =>
    weeklyChurnData.map(w => ({ ...w, churnScaled: toScale(w.churn) })),
    [weeklyChurnData]
  );

  // Generate Y-axis ticks based on max value
  const yTicks = useMemo(() => {
    const maxChurn = Math.max(...weeklyChurnData.map(w => w.churn), 0);
    const ticks = [0, 25, 50, 75, 100];
    if (maxChurn > 100) {
      for (const v of [200, 500, 1000, 2000, 5000]) {
        if (v <= maxChurn * 1.2) ticks.push(toScale(v));
      }
    }
    return ticks;
  }, [weeklyChurnData]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading repos...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>Error: {error}</div>
        <button onClick={() => navigate('/connect')} style={styles.backButton}>
          Back to Connect
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.logo}>B</div>
          <span style={styles.logoText}>Bonzai</span>
          <select
            value={selectedRepo?.id || ''}
            onChange={(e) => {
              const repo = repos.find(r => r.id === parseInt(e.target.value));
              setSelectedRepo(repo);
            }}
            style={{ ...styles.smallSelect, marginLeft: 16, padding: '6px 12px', fontSize: 13 }}
          >
            <option value="">Choose a repository...</option>
            {repos.map((repo) => (
              <option key={repo.id} value={repo.id}>
                {repo.full_name}
              </option>
            ))}
          </select>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.userName}>@{user}</span>
          <button onClick={() => navigate('/connect')} style={styles.backButton}>
            Disconnect
          </button>
        </div>
      </div>

      <div style={styles.content}>

        {selectedRepo && (
          <>
            {/* Loading States */}
            {churnLoading && (
              <div style={{ ...styles.loadingContainer, marginBottom: 24 }}>
                <div style={styles.loadingText}>
                  {loadingProgress.phase} {loadingProgress.total > 0 ? `${loadingProgress.current} / ${loadingProgress.total}` : ''}
                </div>
                <div style={styles.progressBar}>
                  <div style={{
                    ...styles.progressFill,
                    width: loadingProgress.total > 0 ? `${(loadingProgress.current / loadingProgress.total) * 100}%` : '0%'
                  }} />
                </div>
              </div>
            )}

            {/* Hero: Waste + Donut — commit-based, mature weeks only */}
            {!churnLoading && aggregateChurn.totalAdded > 0 && (() => {
              const { featurePct, overallChurn, totalAdded, totalChurned } = aggregateChurn;
              const wastePct = overallChurn;
              const wasteMonthly = Math.round(engBudget * (wastePct / 100));
              const wasteYearly = wasteMonthly * 12;

              // Build donut segments
              const cx = 100, cy = 100, outerR = 88, innerR = 58;
              const featureAngle = (featurePct / 100) * 2 * Math.PI;
              const featurePath = donutArc(cx, cy, outerR, innerR, -Math.PI / 2, -Math.PI / 2 + featureAngle);

              const wasteAngle = (wastePct / 100) * 2 * Math.PI;
              let currentAngle = -Math.PI / 2 + featureAngle;
              const topFilesTotal = fileChurnBreakdown.reduce((s, f) => s + f.pct, 0);
              const otherPct = Math.max(0, 100 - topFilesTotal);

              const wasteSegments = fileChurnBreakdown.map((file, i) => {
                const segAngle = (file.pct / 100) * wasteAngle;
                const path = donutArc(cx, cy, outerR, innerR, currentAngle, currentAngle + segAngle);
                currentAngle += segAngle;
                return { id: file.fullPath, label: file.filename, pctOfWaste: Math.round(file.pct), path, color: RED_SHADES[i] || RED_SHADES[RED_SHADES.length - 1] };
              });

              if (otherPct > 0.5) {
                const segAngle = (otherPct / 100) * wasteAngle;
                const path = donutArc(cx, cy, outerR, innerR, currentAngle, currentAngle + segAngle);
                wasteSegments.push({ id: '_other', label: 'Other files', pctOfWaste: Math.round(otherPct), path, color: '#FEE2E2' });
              }

              return (
                <div style={styles.card}>
                  <div style={styles.heroLayout}>
                    {/* Left: Waste cost + budget */}
                    <div style={styles.heroLeft}>
                      <div style={styles.wasteCostValue}>${wasteYearly.toLocaleString()}</div>
                      <div style={styles.wasteCostLabel}>/year in waste</div>
                      <div style={styles.wasteMonthly}>${wasteMonthly.toLocaleString()}/mo</div>
                      <div style={styles.budgetLine}>
                        of ${Math.round(engBudget).toLocaleString()}/mo engineering budget
                      </div>
                      <button
                        onClick={() => setShowBudgetCalc(!showBudgetCalc)}
                        style={styles.calcButton}
                      >
                        {showBudgetCalc ? 'Done' : 'Calculate Your Budget'}
                      </button>

                      {showBudgetCalc && (
                        <div style={styles.budgetCalc}>
                          {/* Engineers */}
                          <div style={styles.budgetSectionHeader}>
                            <span>Engineers</span>
                            <button onClick={() => setEngineers([...engineers, { salary: 10000, period: 'mo' }])} style={styles.addButton}>+ Add</button>
                          </div>
                          {engineers.map((eng, i) => (
                            <div key={i} style={styles.budgetCalcRow}>
                              <span style={{ fontSize: 13, color: '#374151', fontFamily: mono, minWidth: 90 }}>Engineer {i + 1}</span>
                              <div style={styles.budgetInputWrap}>
                                $<input
                                  type="number"
                                  placeholder="0"
                                  value={eng.salary || ''}
                                  onChange={(e) => { const u = [...engineers]; u[i] = { ...u[i], salary: Number(e.target.value) || 0 }; setEngineers(u); }}
                                  style={styles.budgetInput}
                                />
                                <span
                                  onClick={() => { const u = [...engineers]; u[i] = { ...u[i], period: eng.period === 'mo' ? 'yr' : 'mo' }; setEngineers(u); }}
                                  style={styles.periodToggle}
                                >
                                  /{eng.period || 'mo'}
                                </span>
                                <span onClick={() => setEngineers(engineers.filter((_, j) => j !== i))} style={styles.removeButton}>&times;</span>
                              </div>
                            </div>
                          ))}

                          {/* Tools */}
                          <div style={{ ...styles.budgetSectionHeader, marginTop: 16 }}>
                            <span>Tools</span>
                            <button onClick={() => setTools([...tools, { name: '', cost: 0 }])} style={styles.addButton}>+ Add</button>
                          </div>
                          {tools.map((tool, i) => (
                            <div key={i} style={styles.budgetCalcRow}>
                              <select
                                value={tool.name}
                                onChange={(e) => { const u = [...tools]; u[i] = { ...u[i], name: e.target.value }; setTools(u); }}
                                style={{ ...styles.budgetInput, width: 150, textAlign: 'left', cursor: 'pointer' }}
                              >
                                <option value="">Select tool...</option>
                                {Object.entries(TOOL_CATEGORIES).map(([cat, items]) => (
                                  <optgroup key={cat} label={cat}>
                                    {items.map(item => (
                                      <option key={item} value={item}>{item}</option>
                                    ))}
                                  </optgroup>
                                ))}
                              </select>
                              <div style={styles.budgetInputWrap}>
                                $<input
                                  type="number"
                                  placeholder="0"
                                  value={tool.cost || ''}
                                  onChange={(e) => { const u = [...tools]; u[i] = { ...u[i], cost: Number(e.target.value) || 0 }; setTools(u); }}
                                  style={{ ...styles.budgetInput, width: 70 }}
                                />/mo
                                <span onClick={() => setTools(tools.filter((_, j) => j !== i))} style={styles.removeButton}>&times;</span>
                              </div>
                            </div>
                          ))}

                          {/* Total */}
                          <div style={{ ...styles.budgetCalcRow, borderTop: '1px solid #e5e7eb', paddingTop: 12, marginTop: 12, fontWeight: 600 }}>
                            <span>Total</span>
                            <span style={{ fontFamily: mono }}>${engBudget.toLocaleString()}/mo</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right: Notion-style Donut */}
                    <div style={{ flexShrink: 0, marginLeft: 40, position: 'relative' }}>
                      <svg width="200" height="200" viewBox="0 0 200 200">
                        {/* Background ring */}
                        <circle cx={cx} cy={cy} r={(outerR + innerR) / 2} fill="none" stroke="#f3f4f6" strokeWidth={outerR - innerR} />

                        {/* Feature work arc — green */}
                        <path
                          d={featurePath}
                          fill="#16A34A"
                          opacity={hoveredSegment && hoveredSegment !== '_feature' ? 0.3 : 1}
                          onMouseEnter={() => setHoveredSegment('_feature')}
                          onMouseLeave={() => setHoveredSegment(null)}
                          style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                        />

                        {/* Waste arcs — red shades per file */}
                        {wasteSegments.map((seg) => (
                          <path
                            key={seg.id}
                            d={seg.path}
                            fill={seg.color}
                            opacity={hoveredSegment && hoveredSegment !== seg.id ? 0.3 : 1}
                            onMouseEnter={() => setHoveredSegment(seg.id)}
                            onMouseLeave={() => setHoveredSegment(null)}
                            style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                          />
                        ))}

                        {/* Center text */}
                        <text x={cx} y={cy - 8} textAnchor="middle" style={{ fontSize: 28, fontWeight: 700, fontFamily: mono }} fill="#1a1a1a">
                          {featurePct}%
                        </text>
                        <text x={cx} y={cy + 14} textAnchor="middle" style={{ fontSize: 11, fontFamily: mono }} fill="#9ca3af">
                          feature work
                        </text>
                      </svg>

                      {/* Hover tooltip */}
                      {hoveredSegment && (
                        <div style={{
                          position: 'absolute',
                          bottom: -8,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: '#1a1a1a',
                          color: '#fff',
                          padding: '6px 12px',
                          borderRadius: 6,
                          fontSize: 12,
                          fontFamily: mono,
                          whiteSpace: 'nowrap',
                          zIndex: 10,
                          pointerEvents: 'none',
                        }}>
                          {hoveredSegment === '_feature'
                            ? `Feature work: ${featurePct}%`
                            : (() => {
                                const seg = wasteSegments.find(s => s.id === hoveredSegment);
                                return seg ? `${seg.label}: ${seg.pctOfWaste}% of waste` : '';
                              })()
                          }
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ROI insight */}
                  <div style={styles.roiSection}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 20, fontWeight: 700, fontFamily: mono, color: '#16A34A' }}>{featurePct}%</span>
                      <span style={{ fontSize: 14, color: '#374151' }}>first-pass survival</span>
                      <span style={{ fontSize: 14, color: '#9ca3af' }}>&mdash;</span>
                      <span style={{ fontSize: 20, fontWeight: 700, fontFamily: mono, color: '#DC2626' }}>{wastePct}%</span>
                      <span style={{ fontSize: 14, color: '#374151' }}>iteration overhead</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#6b7280', fontFamily: mono, marginBottom: 6, lineHeight: 1.6 }}>
                      {(totalAdded - totalChurned).toLocaleString()} of {totalAdded.toLocaleString()} lines added survived {churnN} days without being deleted.{' '}
                      <span style={{ color: '#DC2626' }}>{totalChurned.toLocaleString()} lines churned</span> = {wastePct}% waste.
                    </div>
                    <div style={styles.roiHighlight}>
                      At your budget, that's ${wasteYearly.toLocaleString()}/year in velocity you're losing.
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af', fontFamily: mono, marginTop: 6 }}>
                      Based on {aggregateChurn.matureWeekCount} weeks with closed {churnN}-day windows
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Churn Over Time + File Breakdown */}
            {!churnLoading && weeklyChurnData.length > 0 && (
              <div style={styles.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <div>
                    <div style={styles.sectionLabel}>Churn Over Time</div>
                    <div style={{ fontSize: 12, color: '#9ca3af', fontFamily: mono }}>
                      {selectedWeek
                        ? `Week of ${new Date(selectedWeek).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — ${selectedWeekFiles.length} files`
                        : `${churnN}-day survival churn — files are considered churned if modified before the ${churnN}-day mark`
                      }
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {selectedWeek && (
                      <button
                        onClick={() => setSelectedWeek(null)}
                        style={{ padding: '4px 10px', fontSize: 12, color: '#6b7280', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', fontFamily: mono }}
                      >
                        Clear
                      </button>
                    )}
                    <label style={{ fontSize: 12, color: '#6b7280', fontFamily: mono }}>N-day window</label>
                    <input
                      type="number"
                      min={1}
                      max={90}
                      value={churnN}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 1 && val <= 90) setChurnN(val);
                      }}
                      style={{ width: 56, padding: '4px 8px', fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 6, fontFamily: mono, textAlign: 'center', color: '#374151' }}
                    />
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart
                    data={chartData}
                    margin={{ top: 5, right: 10, left: -10, bottom: 0 }}
                    onClick={(state) => {
                      if (state?.activeLabel) {
                        setSelectedWeek(prev => prev === state.activeLabel ? null : state.activeLabel);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <defs>
                      <linearGradient id="churnFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#FF6B35" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="#FF6B35" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="week"
                      tickFormatter={(w) => new Date(w).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      tick={{ fontSize: 11, fill: '#9ca3af', fontFamily: mono }}
                      axisLine={{ stroke: '#f3f4f6' }}
                      tickLine={false}
                    />
                    <YAxis
                      ticks={yTicks}
                      tickFormatter={(v) => `${fromScale(v)}%`}
                      tick={{ fontSize: 11, fill: '#9ca3af', fontFamily: mono }}
                      axisLine={false}
                      tickLine={false}
                      domain={[0, (max) => Math.max(50, max * 1.05)]}
                    />
                    <Tooltip
                      formatter={(v) => [`${fromScale(v)}%`, `${churnN}-day Churn`]}
                      labelFormatter={(w) => new Date(w).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      contentStyle={{ fontSize: 12, fontFamily: mono, borderRadius: 8, border: '1px solid #e5e7eb' }}
                    />
                    <ReferenceLine y={30} stroke="#DC2626" strokeDasharray="6 4" strokeOpacity={0.5} label={{ value: 'Critical', position: 'right', fill: '#DC2626', fontSize: 11, fontFamily: mono }} />
                    <ReferenceLine y={15} stroke="#16A34A" strokeDasharray="6 4" strokeOpacity={0.5} label={{ value: 'Healthy', position: 'right', fill: '#16A34A', fontSize: 11, fontFamily: mono }} />
                    {selectedWeek && (
                      <ReferenceLine x={selectedWeek} stroke="#FF6B35" strokeWidth={2} strokeOpacity={0.6} />
                    )}
                    {/* Gray out immature weeks — window still open */}
                    {(() => {
                      const firstImmature = chartData.find(w => !w.mature);
                      if (!firstImmature) return null;
                      const lastWeek = chartData[chartData.length - 1];
                      return (
                        <ReferenceArea
                          x1={firstImmature.week}
                          x2={lastWeek.week}
                          fill="#f3f4f6"
                          fillOpacity={0.6}
                          label={{ value: 'Pending', position: 'insideTop', fill: '#9ca3af', fontSize: 11, fontFamily: mono }}
                        />
                      );
                    })()}
                    <Area
                      type="monotone"
                      dataKey="churnScaled"
                      stroke="#FF6B35"
                      strokeWidth={2}
                      fill="url(#churnFill)"
                      dot={false}
                      activeDot={{ r: 5, fill: '#FF6B35', stroke: '#fff', strokeWidth: 2, cursor: 'pointer' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>

                {/* Per-week file table */}
                {selectedWeek && (
                  <div style={{ marginTop: 20, borderTop: '1px solid #f3f4f6', paddingTop: 16 }}>
                    {selectedWeekFiles.length > 0 ? (
                      <div style={{ border: '1px solid #f3f4f6', borderRadius: 8, overflow: 'hidden' }}>
                        <div style={styles.tableHeader}>
                          <span style={{ flex: 1 }}>File</span>
                          <span style={styles.tableHeaderCell}>{churnN}d Churn</span>
                          <span style={styles.tableHeaderCell}>+ Added</span>
                          <span style={styles.tableHeaderCell}>- Deleted</span>
                        </div>
                        <div style={styles.tableBody}>
                          {selectedWeekFiles.slice(0, 100).map((file, idx) => (
                            <div key={file.filename} style={styles.tableRow}>
                              <span style={styles.fileName}>
                                <span style={styles.fileIndex}>{idx + 1}</span>
                                {file.filename}
                              </span>
                              <span style={{ ...styles.tableCell, fontWeight: 600, color: file.churnRate > 80 ? '#DC2626' : file.churnRate > 50 ? '#D97706' : '#16A34A' }}>{file.churnRate}%</span>
                              <span style={{ ...styles.tableCell, color: '#16A34A' }}>+{file.additions.toLocaleString()}</span>
                              <span style={{ ...styles.tableCell, color: '#DC2626' }}>-{file.deletions.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: '#9ca3af', fontFamily: mono }}>No files had additions this week</div>
                    )}
                  </div>
                )}
              </div>
            )}

          </>
        )}
      </div>
    </div>
  );
}

const mono = "'JetBrains Mono', monospace";

const styles = {
  container: { minHeight: '100vh', background: '#fff', fontFamily: "'Space Grotesk', -apple-system, sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #e5e7eb' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  logo: { width: 32, height: 32, borderRadius: 6, background: 'linear-gradient(135deg, #FF6B35 0%, #FF3D00 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#fff' },
  logoText: { fontSize: 14, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', color: '#6b7280', fontFamily: mono },
  headerRight: { display: 'flex', alignItems: 'center', gap: 16 },
  userName: { fontSize: 14, color: '#374151', fontFamily: mono },
  backButton: { padding: '8px 16px', fontSize: 13, fontWeight: 500, color: '#6b7280', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', fontFamily: mono },
  content: { maxWidth: 1000, margin: '0 auto', padding: '32px 24px' },
  pageTitle: { fontSize: 28, fontWeight: 700, color: '#1a1a1a', marginBottom: 8, letterSpacing: -0.5 },
  pageSubtitle: { fontSize: 14, color: '#6b7280', marginBottom: 24, fontFamily: mono },
  section: { marginBottom: 24 },
  select: { width: '100%', maxWidth: 400, padding: '12px 16px', fontSize: 16, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif" },
  loading: { textAlign: 'center', padding: 40, color: '#6b7280', fontFamily: mono },
  error: { textAlign: 'center', padding: 40, color: '#DC2626' },

  // Shared card
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 32, marginBottom: 20 },
  sectionLabel: { fontSize: 14, fontWeight: 600, color: '#1a1a1a', marginBottom: 4 },
  smallSelect: { padding: '4px 10px', fontSize: 12, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: 'pointer', fontFamily: mono, color: '#374151' },

  // Loading
  loadingContainer: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 32, textAlign: 'center' },
  loadingText: { fontSize: 14, color: '#374151', marginBottom: 16, fontFamily: mono },
  progressBar: { height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden', maxWidth: 400, margin: '0 auto' },
  progressFill: { height: '100%', background: 'linear-gradient(135deg, #FF6B35 0%, #FF3D00 100%)', borderRadius: 4, transition: 'width 0.3s ease' },

  // Hero layout
  heroLayout: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  heroLeft: { flex: 1 },
  heroRight: { flexShrink: 0, marginLeft: 40 },
  wasteCostValue: { fontSize: 48, fontWeight: 700, color: '#DC2626', fontFamily: mono, lineHeight: 1 },
  wasteCostLabel: { fontSize: 16, color: '#6b7280', marginBottom: 4 },
  wasteMonthly: { fontSize: 14, color: '#9ca3af', fontFamily: mono, marginBottom: 8 },
  budgetLine: { fontSize: 13, color: '#9ca3af', fontFamily: mono, marginBottom: 12 },
  calcButton: { padding: '8px 16px', fontSize: 13, fontWeight: 500, color: '#374151', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', fontFamily: mono },

  // Budget calculator
  budgetCalc: { marginTop: 16, padding: 16, background: '#f9fafb', borderRadius: 8, border: '1px solid #f3f4f6' },
  budgetCalcRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#374151', fontFamily: mono, marginBottom: 8 },
  budgetInputWrap: { display: 'flex', alignItems: 'center', gap: 2, fontFamily: mono, fontSize: 13, color: '#374151' },
  budgetInput: { width: 90, padding: '4px 8px', fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 4, fontFamily: mono, textAlign: 'right' },
  budgetSectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: mono, marginBottom: 8 },
  addButton: { fontSize: 12, color: '#3B82F6', background: 'none', border: 'none', cursor: 'pointer', fontFamily: mono, fontWeight: 500 },
  removeButton: { fontSize: 16, color: '#9ca3af', cursor: 'pointer', marginLeft: 6, lineHeight: 1 },
  periodToggle: { fontSize: 12, color: '#3B82F6', cursor: 'pointer', fontFamily: mono, userSelect: 'none', minWidth: 24 },

  // ROI
  roiSection: { marginTop: 24, paddingTop: 20, borderTop: '1px solid #f3f4f6' },
  roiLine: { fontSize: 14, color: '#374151', marginBottom: 4 },
  roiHighlight: { fontSize: 14, fontWeight: 600, color: '#1a1a1a' },

  // Work breakdown bar
  wbBar: { display: 'flex', height: 24, borderRadius: 6, overflow: 'hidden', background: '#f3f4f6', marginBottom: 12 },
  wbBarSegment: { height: '100%', transition: 'width 0.5s ease' },
  wbLabels: { display: 'flex', gap: 24, marginBottom: 8 },
  wbLabelItem: { display: 'flex', alignItems: 'center', gap: 6 },
  wbDot: { width: 8, height: 8, borderRadius: '50%' },
  wbLabelText: { fontSize: 13, color: '#374151', fontFamily: mono },
  wbMeta: { fontSize: 12, color: '#9ca3af', fontFamily: mono },


  // Chart
  chart: { display: 'flex', gap: 4, alignItems: 'flex-end', height: 120, overflowX: 'auto' },
  chartBar: { display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 36, flex: 1 },
  chartBarInner: { width: '100%', height: 80, background: '#f3f4f6', borderRadius: 4, display: 'flex', alignItems: 'flex-end', overflow: 'hidden' },
  chartBarFill: { width: '100%', borderRadius: 4, transition: 'height 0.3s ease' },
  chartBarLabel: { fontSize: 9, color: '#9ca3af', marginTop: 4, fontFamily: mono, whiteSpace: 'nowrap' },
  chartBarValue: { fontSize: 9, color: '#374151', fontWeight: 600, fontFamily: mono },

  // File table
  tableHeader: { display: 'flex', padding: '10px 12px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: mono },
  tableHeaderCell: { width: 70, textAlign: 'right' },
  tableBody: { maxHeight: 500, overflow: 'auto' },
  tableRow: { display: 'flex', padding: '8px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 12, fontFamily: mono, alignItems: 'center' },
  fileName: { flex: 1, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 },
  fileIndex: { color: '#9ca3af', fontSize: 10, width: 20, flexShrink: 0 },
  tableCell: { width: 70, textAlign: 'right', color: '#6b7280' },
  tableFooter: { padding: '10px 12px', background: '#f9fafb', borderTop: '1px solid #e5e7eb', fontSize: 11, color: '#6b7280', textAlign: 'center', fontFamily: mono },
};

export default Data;
